import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Megaphone, Link2, Loader2, Check, ArrowLeft, AlertTriangle, Upload, Image as ImageIcon } from "lucide-react";
import api, { apiError } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { categoryImage } from "../lib/categoryImages";

const DEPARTMENTS = ["clothing", "groceries", "food", "furniture", "appliances", "electronics", "baby", "housewares", "judaica", "gifts", "toys", "health", "sports", "cars", "mixed"];
const READ_FAIL_MSG = "We could not read this link automatically. Please upload screenshots so we can pull the product details for you.";

const SHOTS = [
  { key: "product", label: "Product screenshot", hint: "Shows the product image, title and price" },
  { key: "variants", label: "Colors & sizes screenshot", hint: "Shows the available colors and sizes" },
  { key: "sizechart", label: "Size chart screenshot", hint: "The retailer's size chart / measurements" },
];

function sizeChartToText(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  return rows.map((r) => {
    if (typeof r !== "object" || r === null) return String(r);
    const size = r.size != null ? `${r.size}: ` : "";
    const rest = Object.entries(r).filter(([k]) => k !== "size").map(([k, v]) => `${k} ${v}`).join(", ");
    return `${size}${rest}`.trim();
  }).join("\n");
}

export default function PostDeal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Screenshot fallback
  const [needShots, setNeedShots] = useState(false);
  const [files, setFiles] = useState({ product: null, variants: null, sizechart: null });
  const [extracting, setExtracting] = useState(false);
  const [urlErr, setUrlErr] = useState("");

  if (!user) { navigate("/auth"); return null; }
  if (!(user.affiliate || user.role === "admin")) {
    return (
      <div className="max-w-lg mx-auto text-center py-20 fade-up">
        <div className="w-16 h-16 rounded-3xl bg-cream flex items-center justify-center mx-auto mb-5"><Megaphone className="text-ink-muted" size={30} /></div>
        <h1 className="font-heading font-black text-2xl text-ink">Partners only</h1>
        <p className="text-ink-soft mt-2">Posting deals is available to approved Affiliate Partners. Contact the SIBLINGO team to become one.</p>
      </div>
    );
  }
  if (user.role !== "admin" && user.may_post_deals === false) {
    return (
      <div className="max-w-lg mx-auto text-center py-20 fade-up" data-testid="post-deal-disabled">
        <div className="w-16 h-16 rounded-3xl bg-cream flex items-center justify-center mx-auto mb-5"><AlertTriangle className="text-ink-muted" size={30} /></div>
        <h1 className="font-heading font-black text-2xl text-ink">Deal posting is turned off</h1>
        <p className="text-ink-soft mt-2">Your partner account can earn commission, but posting deals isn't enabled right now. Ask a SIBLINGO admin to turn on <strong className="text-ink">May Post Deals</strong> for your account.</p>
      </div>
    );
  }

  const set = (k, v) => setPreview((p) => ({ ...p, [k]: v }));

  const fetchPreview = async () => {
    const v = url.trim();
    if (!/^https?:\/\/.+\..+/i.test(v)) { setUrlErr("Enter a valid product URL starting with https:// (e.g. https://www.retailer.com/product/123)."); return; }
    setUrlErr("");
    setLoading(true);
    setPreview(null);
    setNeedShots(false);
    setFiles({ product: null, variants: null, sizechart: null });
    try {
      const { data } = await api.post("/affiliate/preview", { url: url.trim() });
      if (data.verified === false) {
        // Reading failed — switch to the screenshot upload flow.
        setNeedShots(true);
        toast.info(READ_FAIL_MSG);
      } else {
        setPreview({ ...data, department: "mixed", colors: [], sizes: [], size_chart: [], size_chart_text: "", style: "", screenshots: {}, from_screenshots: false });
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || READ_FAIL_MSG);
      setNeedShots(true);
    } finally {
      setLoading(false);
    }
  };

  const pickFile = (key, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image screenshot (JPG, PNG or WEBP)."); e.target.value = ""; return; }
    setFiles((prev) => ({ ...prev, [key]: f }));
  };

  const extractFromShots = async () => {
    if (!files.product || !files.variants || !files.sizechart) {
      toast.error("Please upload all three screenshots first.");
      return;
    }
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("product", files.product);
      fd.append("variants", files.variants);
      fd.append("sizechart", files.sizechart);
      fd.append("url", url.trim());
      const { data } = await api.post("/affiliate/extract-screenshots", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = data.extracted || {};
      setPreview({
        url: url.trim(),
        title: ex.name || "",
        price: ex.price || 0,
        image: data.screenshots?.product || "",
        store: ex.store || "",
        department: DEPARTMENTS.includes(ex.category) ? ex.category : "mixed",
        style: ex.style || "",
        colors: ex.colors || [],
        sizes: ex.sizes || [],
        size_chart: ex.size_chart || [],
        size_chart_text: sizeChartToText(ex.size_chart),
        screenshots: data.screenshots || {},
        from_screenshots: true,
      });
      setNeedShots(false);
      if (data.extracted_ok === false) toast.warning("We couldn't read much from those screenshots — please fill in the details manually below.");
      else toast.success("We pulled the details from your screenshots — please review and confirm below.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Couldn't read the screenshots — please try clearer images.");
    } finally {
      setExtracting(false);
    }
  };

  const submit = async () => {
    if (!preview.title.trim()) { toast.error("A product name is required."); return; }
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/affiliate/deals", {
        url: preview.url || url.trim(),
        title: preview.title,
        price: Number(preview.price) || 0,
        image: preview.image,
        store: preview.store,
        department: preview.department,
        style: preview.style || "",
        colors: (preview.colors || []),
        sizes: (preview.sizes || []),
        size_chart: preview.size_chart || [],
        size_chart_text: preview.size_chart_text || "",
        screenshots: preview.screenshots || {},
        from_screenshots: !!preview.from_screenshots,
      });
      if ((res.blocks || []).length) toast.warning(`Submitted to the admin Listing Approvals queue — flagged: ${res.review_note}`, { duration: 8000 });
      else toast.success("Submitted! Your product is in the admin Listing Approvals queue.");
      navigate("/family-deals");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 fade-up">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-ink-soft hover:text-brand font-semibold text-sm"><ArrowLeft size={16} /> Back</button>
      <div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-ink flex items-center gap-2"><Megaphone className="text-brand" /> Post a Deal</h1>
        <p className="text-ink-soft mt-1">Paste a product link from Amazon, Temu or any store. We'll read the details, then you confirm.</p>
      </div>

      <div className="bg-white rounded-3xl border border-line shadow-soft p-5 space-y-3">
        <label htmlFor="post-deal-url-input" className="text-xs font-bold uppercase tracking-wider text-ink-muted">Product link <span className="text-brand">*</span></label>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Link2 className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted" size={18} />
            <input id="post-deal-url-input" name="product_url" data-testid="post-deal-url-input" type="url" required inputMode="url"
              value={url} onChange={(e) => { setUrl(e.target.value); if (urlErr) setUrlErr(""); }}
              aria-required="true" aria-invalid={!!urlErr} aria-describedby={urlErr ? "post-deal-url-error" : "post-deal-url-help"}
              placeholder="https://www.retailer.com/product/…"
              className={`w-full h-12 pl-11 pr-4 rounded-full border-2 bg-cream outline-none transition-all ${urlErr ? "border-red-500 focus:border-red-500" : "border-line focus:border-brand"}`} />
          </div>
          <button onClick={fetchPreview} disabled={loading} data-testid="post-deal-fetch-btn"
            className="h-12 px-5 rounded-full bg-brand text-white font-bold text-sm hover:bg-brand-hover disabled:opacity-60 transition-all flex items-center gap-2">
            {loading ? <Loader2 className="animate-spin" size={16} /> : "Read link"}
          </button>
        </div>
        {urlErr
          ? <p id="post-deal-url-error" role="alert" data-testid="post-deal-url-error" className="text-xs font-semibold text-red-600">{urlErr}</p>
          : <p id="post-deal-url-help" className="text-xs text-ink-muted">Paste the full retailer product URL (starting with https://). Reading a link only previews it — nothing is submitted or published until you confirm.</p>}
      </div>

      {/* Screenshot upload fallback — only when the link can't be read automatically */}
      {needShots && (
        <div className="bg-white rounded-3xl border border-line shadow-soft p-5 space-y-4" data-testid="post-deal-screenshots">
          <p data-testid="post-deal-review-warning" className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-300 text-amber-800 text-xs font-semibold p-3">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
            {READ_FAIL_MSG}
          </p>
          <div className="space-y-3">
            {SHOTS.map((s) => (
              <div key={s.key} className="rounded-2xl border-2 border-dashed border-line p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blush text-brand flex items-center justify-center shrink-0"><ImageIcon size={18} /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-ink text-sm">{s.label} <span className="text-brand">*</span></p>
                  <p className="text-xs text-ink-muted">{s.hint}</p>
                </div>
                <label data-testid={`shot-upload-${s.key}`} className="h-10 px-3 inline-flex items-center gap-1.5 rounded-xl border-2 border-line bg-white text-xs font-bold text-ink-soft hover:border-brand/40 cursor-pointer transition-all shrink-0">
                  {files[s.key] ? <><Check size={14} className="text-emerald-600" /> Selected</> : <><Upload size={14} /> Choose</>}
                  <input data-testid={`shot-file-${s.key}`} type="file" accept="image/*" onChange={(e) => pickFile(s.key, e)} className="hidden" />
                </label>
              </div>
            ))}
          </div>
          <button onClick={extractFromShots} disabled={extracting || !files.product || !files.variants || !files.sizechart}
            data-testid="post-deal-extract-btn"
            className="w-full h-12 rounded-full bg-brand text-white font-bold text-sm hover:bg-brand-hover disabled:opacity-60 transition-all flex items-center justify-center gap-2">
            {extracting ? <><Loader2 className="animate-spin" size={16} /> Reading screenshots…</> : "Extract product details"}
          </button>
          <p className="text-xs text-ink-muted text-center">We'll save your original link alongside these screenshots.</p>
        </div>
      )}

      {preview && (
        <div className="bg-white rounded-3xl border border-line shadow-soft overflow-hidden" data-testid="post-deal-preview">
          <div className="h-48 bg-blush">
            <img src={categoryImage({ image: preview.image, department: preview.department })} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="p-5 space-y-3">
            {preview.from_screenshots && (
              <p className="flex items-start gap-2 rounded-xl bg-blush border border-brand/20 text-brand text-xs font-semibold p-3">
                <Check size={15} className="shrink-0 mt-0.5" />
                Pulled from your screenshots. Please review and fix anything before submitting.
              </p>
            )}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-ink-muted">Product name</label>
              <input data-testid="post-deal-title" value={preview.title} onChange={(e) => set("title", e.target.value)} className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-line bg-white focus:border-brand outline-none" />
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-muted">Price ($)</label>
                <input data-testid="post-deal-price" type="number" value={preview.price} onChange={(e) => set("price", e.target.value)} className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-line bg-white focus:border-brand outline-none" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-muted">Store</label>
                <input data-testid="post-deal-store" value={preview.store} onChange={(e) => set("store", e.target.value)} className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-line bg-white focus:border-brand outline-none" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-muted">Category</label>
                <select data-testid="post-deal-department" value={preview.department} onChange={(e) => set("department", e.target.value)} className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-line bg-white capitalize">
                  {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-ink-muted">Style</label>
              <input data-testid="post-deal-style" value={preview.style || ""} onChange={(e) => set("style", e.target.value)} placeholder="e.g. casual everyday, dressy" className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-line bg-white focus:border-brand outline-none" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-muted">Colors (comma-separated)</label>
                <input data-testid="post-deal-colors" value={(preview.colors || []).join(", ")} onChange={(e) => set("colors", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="Navy, Cream, Burgundy" className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-line bg-white focus:border-brand outline-none" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-muted">Sizes (comma-separated)</label>
                <input data-testid="post-deal-sizes" value={(preview.sizes || []).join(", ")} onChange={(e) => set("sizes", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="2T, 3T, 4T, 5" className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-line bg-white focus:border-brand outline-none" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-ink-muted">Size chart measurements</label>
              <textarea data-testid="post-deal-sizechart" value={preview.size_chart_text || ""} onChange={(e) => set("size_chart_text", e.target.value)} rows={4} placeholder="e.g.&#10;4: chest 23in, waist 21in, length 18in&#10;5: chest 24in, waist 22in, length 19in" className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-line bg-white focus:border-brand outline-none text-sm leading-relaxed" />
            </div>
            <button onClick={submit} disabled={submitting} data-testid="post-deal-submit-btn"
              className="w-full h-12 rounded-full bg-brand text-white font-bold text-sm hover:bg-brand-hover disabled:opacity-60 transition-all flex items-center justify-center gap-2">
              <Check size={16} /> {submitting ? "Submitting…" : "Confirm & submit for approval"}
            </button>
            <p className="text-xs text-ink-muted text-center">Your deal goes to the SIBLINGO team for review before it appears publicly.</p>
          </div>
        </div>
      )}
    </div>
  );
}
